import React, { useState, useEffect } from 'react';
import ITServicesInvoiceForm from './ITServicesInvoiceForm';
import {
  FaFileInvoice,
  FaSearch,
  FaFilter,
  FaEye,
  FaDownload,
  FaEnvelope,
  FaCalendar,
  FaDollarSign,
  FaUser,
  FaBuilding,
  FaPhone,
  FaCheckCircle,
  FaClock,
  FaTimes,
  FaSpinner,
  FaChevronLeft,
  FaChevronRight,
  FaPlus
} from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/api';
import { PUBLIC_SITE_URL } from '../../constants/branding';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';

const ITServicesInvoiceManagement = ({ onNavigate }) => {
  const { token } = useAdminAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const navigate = useNavigate();

  useEffect(() => {
    loadInvoices();
  }, [token]); // Reload when token changes

  const getHeaders = () => {
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': token } : {}) // HTTP API: No Bearer prefix
    };
  };

  const loadInvoices = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(API_ENDPOINTS.IT_SERVICES_INVOICE, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          action: 'listInvoices'
        })
      });

      const data = await response.json();

      if (data.success) {
        // Sort by date (newest first)
        const sorted = (data.invoices || []).sort((a, b) =>
          new Date(b.createdAt || b.invoiceDate) - new Date(a.createdAt || a.invoiceDate)
        );
        setInvoices(sorted);
      } else {
        setError(data.error || 'Failed to load invoices');
      }
    } catch (err) {
      console.error('Error loading invoices:', err);
      setError('Failed to load invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      paid: <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded text-xs font-semibold">Paid</span>,
      pending: <span className="px-2 py-1 bg-yellow-500/20 text-yellow-300 rounded text-xs font-semibold">Pending</span>,
      overdue: <span className="px-2 py-1 bg-red-500/20 text-red-300 rounded text-xs font-semibold">Overdue</span>,
      cancelled: <span className="px-2 py-1 bg-gray-500/20 text-gray-300 rounded text-xs font-semibold">Cancelled</span>
    };
    return badges[status] || badges.pending;
  };

  // Filter invoices
  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = !searchQuery ||
      invoice.invoiceNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.customerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.customerEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.customerCompany?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedInvoices = filteredInvoices.slice(startIndex, startIndex + itemsPerPage);

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowViewModal(true);
  };

  const handleOpenInvoice = (invoiceId) => {
    if (!invoiceId) return;
    window.open(`${PUBLIC_SITE_URL}/itservices/invoice/${encodeURIComponent(invoiceId)}`, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadInvoice = async (invoiceId, customerEmail) => {
    try {
      const response = await fetch(API_ENDPOINTS.IT_SERVICES_INVOICE, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          action: 'getDownloadUrl',
          invoiceId: invoiceId,
          ...(customerEmail ? { customerEmail } : {})
        })
      });

      const data = await response.json();

      if (data.success && data.url) {
        window.open(data.url, '_blank');
      } else {
        alert(data.error || 'Failed to get download URL');
      }
    } catch (err) {
      console.error('Error downloading invoice:', err);
      alert('Failed to get download URL. Please try again.');
    }
  };

  const handleSendEmail = async (invoiceId) => {
    if (!window.confirm('Send this invoice to the customer via email?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.IT_SERVICES_INVOICE, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          action: 'sendInvoice',
          invoiceId: invoiceId
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('Invoice email sent successfully with PDF attachment!');
      } else {
        alert(data.error || 'Failed to send invoice email');
      }
    } catch (err) {
      console.error('Error sending invoice email:', err);
      alert('Failed to send invoice email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FaSpinner className="animate-spin text-4xl text-[#FFD700]" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#ebdbb2] mb-2">IT Services Invoices</h1>
          <p className="text-[#a89984]">Manage and view all IT Services invoices</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => onNavigate('itservices-quotes')}
            className="bg-[#3c3836] text-[#ebdbb2] border border-[#504945] px-6 py-2 rounded-lg font-semibold hover:bg-[#504945] transition-all flex items-center space-x-2"
          >
            <FaFileInvoice />
            <span>Quotes</span>
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-[#FFD700] to-[#FFB700] text-[#1d2021] px-6 py-2 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center space-x-2"
          >
            <FaPlus />
            <span>Create Payable Invoice</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#282828] rounded-lg p-4 mb-6 flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#a89984]" />
            <input
              type="text"
              placeholder="Search by invoice number, customer name, email, or company..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 bg-[#1d2021] border border-[#504945] rounded text-[#ebdbb2] placeholder-[#a89984] focus:outline-none focus:border-[#FFD700]"
            />
          </div>
        </div>
        <div className="md:w-48">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full px-4 py-2 bg-[#1d2021] border border-[#504945] rounded text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
          >
            <option value="all">All Statuses</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="overdue">Overdue</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/20 border border-red-500 text-red-300 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Invoices Table */}
      <div className="bg-[#282828] rounded-lg overflow-hidden border border-[#504945]">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#1d2021] border-b border-[#504945]">
              <tr>
                <th className="px-6 py-4 text-left text-[#a89984] font-semibold">Invoice Number</th>
                <th className="px-6 py-4 text-left text-[#a89984] font-semibold">Customer</th>
                <th className="px-6 py-4 text-left text-[#a89984] font-semibold">Date</th>
                <th className="px-6 py-4 text-left text-[#a89984] font-semibold">Amount</th>
                <th className="px-6 py-4 text-left text-[#a89984] font-semibold">Status</th>
                <th className="px-6 py-4 text-left text-[#a89984] font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#504945]">
              {paginatedInvoices.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-[#a89984]">
                    {searchQuery || statusFilter !== 'all' ? 'No invoices found matching your filters' : 'No invoices found'}
                  </td>
                </tr>
              ) : (
                paginatedInvoices.map((invoice) => (
                  <tr key={invoice.invoiceId} className="hover:bg-[#504945] transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-[#ebdbb2] font-medium">{invoice.invoiceNumber || invoice.invoiceId}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-[#ebdbb2] font-medium">{invoice.customerName}</div>
                      <div className="text-[#a89984] text-sm">{invoice.customerEmail}</div>
                      {invoice.customerCompany && (
                        <div className="text-[#a89984] text-sm">{invoice.customerCompany}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-[#ebdbb2]">{formatDate(invoice.invoiceDate || invoice.createdAt)}</div>
                      {invoice.dueDate && (
                        <div className="text-[#a89984] text-xs">Due: {formatDate(invoice.dueDate)}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-[#ebdbb2] font-semibold">{formatCurrency(invoice.totalAmount)}</div>
                      {invoice.paidAmount > 0 && (
                        <div className="text-[#a89984] text-xs">Paid: {formatCurrency(invoice.paidAmount)}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(invoice.status || invoice.paymentStatus)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleOpenInvoice(invoice.invoiceId)}
                          className="p-2 bg-blue-500/20 text-blue-300 rounded hover:bg-blue-500/30 transition-colors"
                          title="View Invoice"
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => handleDownloadInvoice(invoice.invoiceId, invoice.customerEmail)}
                          className="p-2 bg-purple-500/20 text-purple-300 rounded hover:bg-purple-500/30 transition-colors"
                          title="Download PDF"
                        >
                          <FaDownload />
                        </button>
                        <button
                          onClick={() => handleSendEmail(invoice.invoiceId)}
                          className="p-2 bg-green-500/20 text-green-300 rounded hover:bg-green-500/30 transition-colors"
                          title="Send Email"
                        >
                          <FaEnvelope />
                        </button>
                        {invoice.quoteId && (
                          <button
                            onClick={() => navigate(`/?tab=itservices-quotes`)}
                            className="p-2 bg-purple-500/20 text-purple-300 rounded hover:bg-purple-500/30 transition-colors"
                            title="View Quote"
                          >
                            <FaFileInvoice />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-[#282828] px-6 py-4 flex items-center justify-between border-t border-[#504945]">
            <div className="text-[#a89984] text-sm">
              Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredInvoices.length)} of {filteredInvoices.length} invoices
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 bg-[#1d2021] border border-[#504945] rounded text-[#ebdbb2] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#504945] transition-colors"
              >
                <FaChevronLeft />
              </button>
              <span className="text-[#ebdbb2] px-4">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 bg-[#1d2021] border border-[#504945] rounded text-[#ebdbb2] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#504945] transition-colors"
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Create Invoice Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-[#1d2021] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto border border-[#3c3836] shadow-2xl relative">
            <div className="sticky top-0 z-10 bg-[#1d2021]/95 backdrop-blur-md px-8 py-6 border-b border-[#3c3836] flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-[#ebdbb2]">Create New Payable Invoice</h2>
                <p className="text-[#a89984] text-sm mt-1">Generate a standalone tax invoice for your customer</p>
              </div>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 hover:bg-[#3c3836] rounded-full text-[#a89984] transition-colors"
              >
                <FaTimes size={24} />
              </button>
            </div>
            <div className="p-8">
              <ITServicesInvoiceForm
                onSave={(newInvoice) => {
                  setShowCreateModal(false);
                  loadInvoices();
                  // Optionally show download/email link immediately
                  if (window.confirm(`Invoice ${newInvoice.invoiceNumber} created! Would you like to send it to the customer via email now?`)) {
                    handleSendEmail(newInvoice.invoiceId);
                  } else if (window.confirm(`Would you like to download it now instead?`)) {
                    handleDownloadInvoice(newInvoice.invoiceId, newInvoice.customerEmail);
                  }
                }}
                onCancel={() => setShowCreateModal(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ITServicesInvoiceManagement;

